import { useEffect, useState } from 'react';

import { actions } from '../models';
import { useAppDispatch, useAppSelector } from '../store';
import type { NomineeType, PollType } from '../types';
import LocalStorageClient from '../utils/LocalStorageClient';

import { usePollStatus } from './usePollStatus';

/**
 * Custom hook for managing the state and behavior of the vote "like" button.
 *
 * @param {PollType} poll - The poll object.
 * @param {NomineeType} nominee - The nominee object.
 * @returns {Object} The state and behavior of the "like" button.
 */
export const useLikeButtonState = (poll: PollType, nominee: NomineeType) => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const [isVoted, setIsVoted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isButtonHidden, setIsButtonHidden] = useState(true);
  const { pollIsOpen, pollIsClosed, pollIsWinner } = usePollStatus(poll);

  useEffect(() => {
    const nomineeVoted = LocalStorageClient.nomineeIsVoted(poll, nominee);
    setIsVoted(nomineeVoted);
    // vote button is disabled if ONE of this condition is TRUE
    // first condition poll is open,
    // second condition: this nominee is voted
    const buttonShouldBeDisabled = pollIsOpen && !user?.alreadyVoted;
    setIsButtonDisabled(!buttonShouldBeDisabled);

    // button is hidden if the poll is closed.
    // Checkmark appears if the poll is closed and this nominee is voted.
    const buttonShouldBeHidden = pollIsClosed || pollIsWinner;
    setIsButtonHidden(buttonShouldBeHidden);
    //eslint-disable-next-line
  }, [user?.alreadyVoted]);

  useEffect(() => {
    dispatch(actions.user.setUserVoted(poll));
  }, [poll, dispatch]);

  return { isVoted, isButtonDisabled, isButtonHidden };
};
